import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { ReportTheme } from '../../types';
import { getChartColor } from './chartUtils';
import { validateDateKeyedData, filterDataByDateRange, sortChartDataByDate } from './functionalWidgets';

interface UserAcquisitionLineChartWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const UserAcquisitionLineChartWidget: React.FC<UserAcquisitionLineChartWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {
  console.log('UserAcquisitionLineChartWidget raw data:', data);

  // Process the raw API data internally for Users vs New Users
  const processData = () => {
    // Validate data structure and get entries
    const validation = validateDateKeyedData(data);
    if (!validation.isValid) {
      console.warn(`UserAcquisitionLineChartWidget: ${validation.error}`);
      return null;
    }

    // Filter data by date range
    const filteredEntries = filterDataByDateRange(validation.entries, report_start_date, report_end_date);
    
    if (filteredEntries.length === 0) {
      console.warn('UserAcquisitionLineChartWidget: No data entries found in date range');
      return null;
    }

    const firstEntry = filteredEntries[0];
    const [, firstDayData] = firstEntry;

    if (typeof (firstDayData as any).users !== 'number' || typeof (firstDayData as any).new_users !== 'number') {
      console.warn('UserAcquisitionLineChartWidget: Missing required metrics (users, new_users)');
      return null;
    }
    
    const chartData = filteredEntries.map(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object' || !dayData.date) {
        return null;
      }

      return {
        name: new Date(dayData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        users: dayData.users || 0,
        new_users: dayData.new_users || 0,
        date: dayData.date // Keep original date for sorting
      };
    }).filter((item: any): item is { name: string; users: number; new_users: number; date: string } => item !== null);
    
    // Sort by date using utility function
    const sortedData = sortChartDataByDate(chartData);
    return sortedData.length > 0 ? sortedData : null;
  };

  const processedData = processData();

  if (!processedData || processedData.length === 0) {
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={processedData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Line 
          type="monotone" 
          dataKey="users" 
          stroke={getChartColor(0, reportTheme.theme)} 
          strokeWidth={2} 
          name="Total Users"
          dot={false}
          activeDot={false}
        />
        <Line 
          type="monotone" 
          dataKey="new_users" 
          stroke={getChartColor(1, reportTheme.theme)} 
          strokeWidth={2} 
          name="New Users"
          dot={false}
          activeDot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
